import { Injectable } from '@angular/core';
import { map, Observable, switchMap, throwError, of } from 'rxjs';
import { Course, Hole, TeeBox } from '../../models';
import {
  Configuration,
  CoursesService,
  ModulesCoursesEndpointsContractsCoursesCreateCourseRequest,
  ModulesCoursesEndpointsContractsCoursesUpdateCourseRequestModel,
  ModulesCoursesEndpointsContractsHoleYardagesCreateHoleYardageRequestModel,
  ModulesCoursesEndpointsContractsHolesCreateHoleRequestModel,
  ModulesCoursesEndpointsContractsHolesUpdateHoleRequestModel,
  ModulesCoursesEndpointsContractsHoleYardagesUpdateHoleYardageRequestModel,
  ModulesCoursesEndpointsContractsTeeboxesCreateTeeboxRequestModel,
} from '../../api-client';
import { HttpClient } from '@angular/common/http';
import { HoleYardage } from '../../models/holeYardage';
import { ConfigService } from '../config/config.service';
import { NavigationRoute } from '../../models/config';

export const DEFAULT_TEEBOX_NAME = 'Professional';
export const DEFAULT_TEEBOX_COLOR = 'N/A';

@Injectable({
  providedIn: 'root',
})
export class CourseService extends CoursesService {
  constructor(
    httpClient: HttpClient,
    config: Configuration,
    configService: ConfigService
  ) {
    super(httpClient, config.basePath!, config);

    // get tournament navItem from config navigation
    this.tournamentsNavItem = configService.config?.navigation?.find(
      (item) => item.name === 'Tournaments'
    );
    this.coursesNavItem = configService.config?.navigation?.find(
      (item) => item.name === 'Courses'
    );
  }

  tournamentsNavItem: NavigationRoute | undefined = undefined;
  coursesNavItem: NavigationRoute | undefined = undefined;

  /**
   * Retrieve all courses
   */
  getAll(): Observable<Course[]> {
    return super.getCoursesEndpoint().pipe(
      map((courses: Course[]) =>
        courses.map((course, index) => ({
          ...course,
          id: index,
        }))
      )
    );
  }

  /**
   * Get course by ID (no caching)
   */
  getById(courseId: string): Observable<Course> {
    return super.getCourseByIdEndpoint(courseId);
  }

  /**
   * Retrieve holes for a course
   */
  getHolesByCourse(courseId: string): Observable<Hole[]> {
    return super.getHolesByCourseIdEndpoint(courseId);
  }

  /**
   * Retrieve tee boxes for a course
   */
  getTeeBoxesByCourse(courseId: string): Observable<TeeBox[]> {
    return super.getTeeboxesByCourseIdEndpoint(courseId);
  }

  getHoleYardagesByCourse(courseId: string): Observable<HoleYardage[]> {
    return super.getHoleYardagesByCourseIdEndpoint(courseId);
  }

  createCourse(course: { name: string; shortName?: string; image?: string; tournamentId?: string; lpgaId?: number }): Observable<string> {
    if (this.coursesNavItem?.readonly) {
      return throwError(() => new Error('Course creation is disabled in readonly mode.'));
    }

    const request = {
      name: course.name,
      shortName: course.shortName,
      image: course.image ?? '',
      tournamentId: course.tournamentId,
      lpgaId: course.lpgaId,
    } as ModulesCoursesEndpointsContractsCoursesCreateCourseRequest & { tournamentId?: string };

    return super.coursesPost(request);
  }

  createHole(courseId: string, hole: Partial<Hole>): Observable<string> {
    if (this.coursesNavItem?.readonly) {
      return throwError(() => new Error('Course creation is disabled in readonly mode.'));
    }

    const request: ModulesCoursesEndpointsContractsHolesCreateHoleRequestModel = {
      number: hole.number,
      par: hole.par,
      handicap: hole.handicap,
      isDriveMeasured: hole.isDriveMeasured,
      image: hole.image,
    };

    return super.coursesCourseIdHolesPost(courseId, request);
  }

  createTeebox(
    courseId: string,
    teeBox: {
      name: string;
      color?: string;
      slopeRating?: number;
      courseRating?: number;
    }
  ): Observable<string> {
    if (this.coursesNavItem?.readonly) {
      return throwError(() => new Error('Course creation is disabled in readonly mode.'));
    }

    const normalizedName = (teeBox.name ?? '').trim();
    const normalizedColor = (teeBox.color ?? '').trim();

    const request: ModulesCoursesEndpointsContractsTeeboxesCreateTeeboxRequestModel = {
      name: normalizedName || DEFAULT_TEEBOX_NAME,
      color: normalizedColor || DEFAULT_TEEBOX_COLOR,
      slopeRating: teeBox.slopeRating,
      courseRating: teeBox.courseRating,
    };

    return super.coursesCourseIdTeeboxesPost(courseId, request).pipe(
      switchMap((id) => {
        if (id === null || id === undefined) {
          return throwError(() => new Error('Failed to create tee box.'));
        }

        return of(id.toString());
      })
    );
  }

  createHoleYardage(courseId: string, holeId: string, holeYardage: HoleYardage): Observable<HoleYardage> {
    if (this.coursesNavItem?.readonly) {
      return throwError(() => new Error('Course creation is disabled in readonly mode.'));
    }

    const request: ModulesCoursesEndpointsContractsHoleYardagesCreateHoleYardageRequestModel = {
      roundId: holeYardage.roundId,
      yardage: holeYardage.yardage,
      teeboxId: holeYardage.teeboxId,
    };

    return super
      .coursesCourseIdHolesHoleIdHoleYardagesPost(courseId, holeId, request)
      .pipe(
        map((created) => ({
          ...created,
          holeNumber: holeYardage.holeNumber,
          courseId,
          holeId,
        }) as HoleYardage)
      );
  }

  updateCourse(course: Course): Observable<Course> {
    // If readonly mode is enabled, just return the course by ID without updating
    if (this.coursesNavItem?.readonly) {
      return super.getCourseByIdEndpoint(course.courseId!);
    }

    const updateRequest: ModulesCoursesEndpointsContractsCoursesUpdateCourseRequestModel = {
      name: course.name,
      shortName: course.shortName,
      image: course.image,
      lpgaId: course.lpgaId,
    };

    return super.coursesCourseIdPut(course.courseId!, updateRequest).pipe(
      switchMap(() => super.getCourseByIdEndpoint(course.courseId!))
    );
  }

  saveHole(hole: Hole): Observable<Hole> {
    // If readonly mode is enabled, just return the hole by ID without updating
    if (this.coursesNavItem?.readonly) {
      return super.getHoleByIdEndpoint(hole.holeId!, hole.courseId!);
    }

    const updateRequest = {
      number: hole.number,
      par: hole.par,
      handicap: hole.handicap,
      isDriveMeasured: hole.isDriveMeasured,
      image: hole.image,
    } as ModulesCoursesEndpointsContractsHolesUpdateHoleRequestModel;

    return super
      .coursesCourseIdHolesHoleIdPut(
        hole.holeId!,
        hole.courseId!,
        updateRequest
      )
      .pipe(
        switchMap(() => super.getHoleByIdEndpoint(hole.holeId!, hole.courseId!))
      );
  }

  saveHoleYardage(holeYardage: HoleYardage): Observable<HoleYardage> {
    // If readonly mode is enabled, just return the hole yardage by ID without updating
    if (this.coursesNavItem?.readonly) {
      return this.getHoleYardageByIdEndpoint(
        holeYardage.holeId!,
        holeYardage.courseId!,
        holeYardage.holeYardageId!
      );
    }

    const updateRequest = {
      teeboxId: holeYardage.teeboxId,
      roundId: holeYardage.roundId,
      yardage: holeYardage.yardage,
    } as ModulesCoursesEndpointsContractsHoleYardagesUpdateHoleYardageRequestModel;

    return super
      .coursesCourseIdHolesHoleIdHoleYardagesHoleYardageIdPut(
        holeYardage.holeId!,
        holeYardage.courseId!,
        holeYardage.holeYardageId!,
        updateRequest
      )
      .pipe(
        switchMap(() =>
          this.getHoleYardageByIdEndpoint(
            holeYardage.holeId!,
            holeYardage.courseId!,
            holeYardage.holeYardageId!
          )
        )
      );
  }
}
